import React, { useEffect, useMemo, useState } from 'react';
import { ErrorAlert } from '../../../shared/components/ErrorAlert';
import { useAuth } from '../../../contexts/AuthContext';
import { inviteMerchant } from '../../../services/merchants/adminMerchantApi';
import type { AdminCompanyInvitation } from '../../../models/adminCompany';

export interface PendingInvitationsSectionProps {
  invitations: AdminCompanyInvitation[];
  companyId: string;
  onResent: () => void;
}

/** Invitations shown per page. */
const PAGE_SIZE = 10;

function formatDateTime(value: string | null): string {
  if (!value) {
    return '—';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString();
}

/** Company-scoped pending/expired merchant invitations, with a resend action per row. */
export function PendingInvitationsSection({
  invitations,
  companyId,
  onResent,
}: PendingInvitationsSectionProps): React.ReactElement {
  const { acquireAccessToken } = useAuth();
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(invitations.length / PAGE_SIZE));

  // Keep the current page within range when the list shrinks (e.g. after a reload).
  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const pageInvitations = useMemo(
    () => invitations.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [invitations, page]
  );

  const handleResend = async (invitation: AdminCompanyInvitation): Promise<void> => {
    setError(null);
    setResendingId(invitation.id);
    try {
      await inviteMerchant(
        {
          email: invitation.email,
          suggestedMerchantName: invitation.suggestedMerchantName,
          companyId,
        },
        acquireAccessToken
      );
      onResent();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to resend invitation.');
    } finally {
      setResendingId(null);
    }
  };

  return (
    <div className="bo-card">
      <div className="bo-admin-table-head">
        <h2 className="bo-admin-section-title">Pending invitations</h2>
        <p className="bo-muted">Merchant invitations awaiting acceptance for this company.</p>
      </div>
      {error ? <ErrorAlert message={error} /> : null}
      {invitations.length === 0 ? (
        <p className="bo-muted">No pending invitations for this company.</p>
      ) : (
        <div className="bo-admin-table-wrap">
          <table className="bo-admin-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Suggested name</th>
                <th>Status</th>
                <th>Expires</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {pageInvitations.map((invitation) => {
                const isResending = resendingId === invitation.id;
                const isPending = invitation.status === 'Pending';
                return (
                  <tr key={invitation.id}>
                    <td>{invitation.email || '—'}</td>
                    <td>{invitation.suggestedMerchantName || '—'}</td>
                    <td>
                      <span
                        className={`bo-status-pill ${isPending ? 'bo-status-active' : 'bo-status-inactive'}`}
                      >
                        {invitation.status}
                      </span>
                    </td>
                    <td>{formatDateTime(invitation.invitationExpiresAt)}</td>
                    <td>
                      <div className="bo-actions">
                        <button
                          type="button"
                          className="bo-btn bo-btn-secondary"
                          onClick={() => void handleResend(invitation)}
                          disabled={isResending || isPending}
                          title={isPending ? 'Still pending — resend is available once it expires.' : undefined}
                        >
                          {isResending ? 'Resending…' : 'Resend'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {invitations.length > PAGE_SIZE ? (
        <div className="bo-admin-pagination">
          <span className="bo-muted">
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, invitations.length)} of{' '}
            {invitations.length}
          </span>
          <div className="bo-admin-pagination-controls">
            <button
              type="button"
              className="bo-btn bo-btn-secondary"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={page <= 1}
            >
              Previous
            </button>
            <span className="bo-muted">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              className="bo-btn bo-btn-secondary"
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={page >= totalPages}
            >
              Next
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
